import { useEffect, useRef } from "react";

export default function ParticleField() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const mouse = { x: -9999, y: -9999 };

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    window.addEventListener("mousemove", onMouseMove);

    const COUNT       = 38;
    const COLOR       = "201, 169, 110"; // #c9a96e
    const CONNECT_D   = 130;
    const MOUSE_D     = 160;

    const particles = Array.from({ length: COUNT }, () => ({
      x:     Math.random() * canvas.width,
      y:     Math.random() * canvas.height,
      vx:    (Math.random() - 0.5) * 0.35,
      vy:    (Math.random() - 0.5) * 0.35,
      r:     Math.random() * 1.2 + 0.4,
      alpha: Math.random() * 0.22 + 0.07,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Connecting lines between nearby particles
      for (let i = 0; i < COUNT; i++) {
        for (let j = i + 1; j < COUNT; j++) {
          const dx   = particles[i].x - particles[j].x;
          const dy   = particles[i].y - particles[j].y;
          const dist = Math.hypot(dx, dy);
          if (dist < CONNECT_D) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(${COLOR}, ${0.07 * (1 - dist / CONNECT_D)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }

        // Lines to mouse cursor
        const mdx   = particles[i].x - mouse.x;
        const mdy   = particles[i].y - mouse.y;
        const mdist = Math.hypot(mdx, mdy);
        if (mdist < MOUSE_D) {
          ctx.beginPath();
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.strokeStyle = `rgba(${COLOR}, ${0.18 * (1 - mdist / MOUSE_D)})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }

      // Update & draw particles
      for (const p of particles) {
        // Gentle mouse attraction
        const mdx   = mouse.x - p.x;
        const mdy   = mouse.y - p.y;
        const mdist = Math.hypot(mdx, mdy);
        if (mdist < MOUSE_D) {
          p.vx += (mdx / mdist) * 0.012;
          p.vy += (mdy / mdist) * 0.012;
        }

        // Speed cap
        const speed = Math.hypot(p.vx, p.vy);
        if (speed > 0.75) { p.vx *= 0.96; p.vy *= 0.96; }

        p.x += p.vx;
        p.y += p.vy;

        // Wrap around edges
        if (p.x < 0)            p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0)            p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${COLOR}, ${p.alpha})`;
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
}
